import { useCallback, useEffect, useRef, useState } from 'react'

interface UseNoticeScrollHighlightOptions {
  /** Duration of highlight effect in ms. Defaults to 1500. */
  highlightDuration?: number
  /** Delay before highlight starts when scrolling. Defaults to 650. */
  scrollDelay?: number
}

interface UseNoticeScrollHighlightReturn {
  /** ID of currently highlighted notice, or null */
  highlightedNoticeId: string | null
  /** Callback to scroll to a notice and trigger highlight */
  scrollToNotice: (noticeId: string) => void
}

/**
 * Hook for scrolling to a notice card with a temporary highlight effect
 *
 * Similar to useScrollHighlight but designed for multiple potential targets.
 * Each notice card should use `notice-card-${noticeId}` as its element ID.
 *
 * Handles:
 * - Smooth scrolling to notice if not in view
 * - Delayed highlight after scroll animation
 * - Immediate highlight if already in view
 * - Auto-removal of highlight after duration
 * - Cleanup of timeouts on unmount
 */
export function useNoticeScrollHighlight({
  highlightDuration = 1500,
  scrollDelay = 650,
}: UseNoticeScrollHighlightOptions = {}): UseNoticeScrollHighlightReturn {
  const [highlightedNoticeId, setHighlightedNoticeId] = useState<string | null>(
    null,
  )
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  const scrollToNotice = useCallback(
    (noticeId: string) => {
      const el = document.getElementById(`notice-card-${noticeId}`)
      if (!el) return

      const rect = el.getBoundingClientRect()
      const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight

      // Clear any existing timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }

      // Clear any existing highlight
      setHighlightedNoticeId(null)

      // Scroll if not in view
      if (!isInView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      // Delay highlight if scrolling, immediate if already in view
      const glowDelay = isInView ? 0 : scrollDelay

      timeoutRef.current = setTimeout(() => {
        setHighlightedNoticeId(noticeId)
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedNoticeId(null)
        }, highlightDuration)
      }, glowDelay)
    },
    [highlightDuration, scrollDelay],
  )

  return { highlightedNoticeId, scrollToNotice }
}
